/* eslint-disable comma-dangle */
import { Dialog, DialogTitle, IconButton, DialogContent, Typography, DialogActions, Button, TextField, Autocomplete, FormControl, Switch, Box, Avatar, Stack } from '@mui/material'; 
import CloseIcon from '@mui/icons-material/Close';
import { ReportsBuilderContext, ReportBuilderValues } from '../context/ReportsBuilderContext';
import { useContext } from 'react';  
import { File06 } from '../../../assets';
import { theme } from '../../../theme';
import DatePickerLocalized from '../../../components/shared/DatePickerLocalized';

const GenerateReport = () => {
  const { 
    t, 
    fromDate,
    toDate,
    handleClose, 
    getMaxDate,
    getMinDate,
    openGenerateDialog, 
    user,
    handleSelectChange,
    handleSelectAllChange,
    selectedStores,
    checkedAllStores,
    handleGenerateReport, 
    isGenerateDisabled,
    reportName,
    setFromDate,
    setFromDateError,
    fromDateError,
    setToDate,
    setToDateError,
    toDateError,

  } = useContext(ReportsBuilderContext) as ReportBuilderValues;

  const reportTitle = `${t('previewReport')} - ${reportName}`;

  return (
    <Dialog
      fullWidth
      maxWidth='sm'
      open={openGenerateDialog}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      sx={{
        '& .MuiDialog-container': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar
            sx={{
              backgroundColor: () => theme.palette.secondary[100],
              color: () => theme.palette.secondary[700],
            }}
          >
            <File06 color={theme.palette.secondary[500]} />
          </Avatar>
        </Box> 
        <IconButton
          onClick={handleClose}
          sx={{
            color: (theme) => theme.palette.custom.gray[800],
            mr: '-10px',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle> 
      <DialogContent>
        <Stack>
          <Typography variant='textLG'
            fontWeight={600}> {reportTitle}</Typography>
          <Typography color="textSecondary"
            sx={{ mt: 1 , whiteSpace: 'pre-line' }}
            variant="textSM">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '12px', flexDirection: { xs: 'column', sm: 'row' } }}> 
              <Box sx={{ display: 'flex', gap: '12px', pt: '10px', width: '100%', flexDirection: { xs: 'column', sm: 'row' } }}>
                <Box sx={{ flex: 1, color: (theme) => theme.palette.custom.gray[600] }}>
                  <DatePickerLocalized
                    useCalendar={false}
                    label={t('fromDate')}
                    value={fromDate}
                    translations={{
                      clearButtonLabel: t('calendar.clearButtonLabel'),
                      todayButtonLabel: t('calendar.todayButtonLabel'),
                      cancelButtonLabel: t('calendar.cancelButtonLabel'),
                      okButtonLabel: t('calendar.okButtonLabel'),
                    }}
                    onChange={(newValue) => {
                      if (newValue == '') {
                        setFromDate('');
                      }
                      if (newValue) {
                        setFromDate(newValue);
                        setFromDateError(false);
                      }
                    }}
                    error={fromDateError}
                    shouldDisableDate={(date) => {
                      const minDate = getMinDate();
                      const maxDate = toDate !== '' ? toDate : getMaxDate();
                      return date < minDate || date > maxDate;
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1, color: (theme) => theme.palette.custom.gray[600] }}>
                  <DatePickerLocalized
                    useCalendar={false}
                    label={t('toDate')}
                    value={toDate}
                    translations={{
                      clearButtonLabel: t('calendar.clearButtonLabel'),
                      todayButtonLabel: t('calendar.todayButtonLabel'),
                      cancelButtonLabel: t('calendar.cancelButtonLabel'),
                      okButtonLabel: t('calendar.okButtonLabel'),
                    }}
                    onChange={(newValue) => {
                      if (newValue == '') {
                        setToDate('');
                      } else if (newValue) {
                        setToDate(newValue);
                        setToDateError(false);
                      }
                    }}
                    error={toDateError}
                    shouldDisableDate={(date) => {
                      const maxDate = getMaxDate();
                      const minDate = fromDate !== '' ? fromDate : getMinDate();
                      return date < minDate || date > maxDate;
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: '12px', width: '100%', flexDirection: { xs: 'column', sm: 'row' } }}>
                <Box sx={{ flex: 1, color: (theme) => theme.palette.custom.gray[600], maxWidth: { xs: '100%', sm: 270 } }}>
                  <FormControl fullWidth>
                    <Autocomplete
                      fullWidth
                      multiple
                      limitTags={2}
                      options={user?.stores ?? []}
                      value={selectedStores}
                      disabled={checkedAllStores}
                      onChange={handleSelectChange}
                      getOptionKey={(option) => option.storeNumber ?? ''}
                      getOptionLabel={(option) => option.storeName ?? ''}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required={selectedStores.length === 0 && !checkedAllStores}
                          label={t('selectStores')}
                          placeholder={t('selectStores')}
                        />
                      )}
                    />
                  </FormControl>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <Typography>{t('allStore')}</Typography>
                  <Switch
                    checked={checkedAllStores}
                    disabled={selectedStores.length > 0}
                    onChange={handleSelectAllChange}
                  />
                </Box>
              </Box>
            </Box> 
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ 
        padding: '0px 24px 24px', 
        '&.MuiDialogActions-root': {
          padding: '0px 24px 24px' 
        }
      }}>
        <Stack direction={{ xs: 'column', md: 'row'  }}
          gap='12px'
          width='100%'>
          <Button
            fullWidth
            variant="secondary"
            sx={{ fontSize: '16px' }} 
            size="md"
            onClick={handleClose}>
            {t('close')}
          </Button>
          <Button
            disabled={isGenerateDisabled}
            fullWidth
            variant="primary"
            sx={{ fontSize: '16px' }} 
            size="md"
            onClick={() => handleGenerateReport()}
          >
            {t('preview')}
          </Button>
      
        </Stack>
      </DialogActions>
    </Dialog > 
  );
};

export default GenerateReport; 